import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type NotificationAction = "dismissed" | "doNotShowAgain";

type NotificationSettings = Record<string, NotificationAction>;

interface ChangelogNotification {
  id: string;
  publishedAt: string;
}

interface NotificationSettingsStore {
  settings: NotificationSettings;
  setNotificationAction: (key: string, action: NotificationAction) => void;
  getNotificationAction: (key: string) => NotificationAction | undefined;
  clearNotificationAction: (key: string) => void;
  // Changelog notification tracking
  changelog: ChangelogNotification | null;
  setChangelogNotification: (changelog: ChangelogNotification) => void;
  shouldShowChangelog: (
    latestChangelogId: string,
    latestPublishedAt: string
  ) => boolean;
}

export const useNotificationSettingsStore = create<NotificationSettingsStore>()(
  persist(
    (set, get) => ({
      // Changelog notification tracking
      changelog: null,

      clearNotificationAction: (key: string) => {
        set((state) => {
          const newSettings = { ...state.settings };
          delete newSettings[key];
          return { settings: newSettings };
        });
      },

      getNotificationAction: (key: string) => get().settings[key],

      setChangelogNotification: (changelog: ChangelogNotification) => {
        set({ changelog });
      },

      setNotificationAction: (key: string, action: NotificationAction) => {
        set((state) => ({
          settings: {
            ...state.settings,
            [key]: action,
          },
        }));
      },
      settings: {},

      shouldShowChangelog: (
        latestChangelogId: string,
        latestPublishedAt: string
      ) => {
        const current = get().changelog;
        if (!current) {
          return true;
        }
        // Show if ID is different AND the new changelog is newer
        if (current.id !== latestChangelogId) {
          return new Date(latestPublishedAt) > new Date(current.publishedAt);
        }
        return false;
      },
    }),
    {
      name: "bezoned-notification-settings",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
